import { ConstantValues } from "common/constants";
import { saveAs } from "file-saver";
import { API_BASE, DIAGNOSTICS_API_BASE } from "./config";
import store from "./store";
import { notifyIfProjectIqActivity } from "./utils/projectIqActivity";

export function UserNeedsToLogin() {
  // Intentional no-op: login redirect is handled by the auth middleware
}

UserNeedsToLogin.prototype = new Error();

export const unAuthRequest = async (url, options = {}) => {
  try {
    const response = await fetch(url, {
      ...options,
      credentials: "include",
    });
    return response;
  } catch (error) {
    return {
      error: true,
      status: 500,
      message: "Network error. Please try again later.",
    };
  }
};

export const requestWithCookieAuth = async (url, options = {}) => {
  try {
    let response = await fetch(url, {
      ...options,
      credentials: "include",
    });

    if (response.ok) {
      localStorage.setItem(
        ConstantValues.SESSION_TIMESTAMP,
        String(Math.floor(Date.now() / 1000)),
      );
    }

    if (response.status === 401) {
      const refreshResponse = await fetch(`${API_BASE}/auth/refresh-token/`, {
        method: "GET",
        credentials: "include",
      });

      if (refreshResponse.ok) {
        response = await fetch(url, {
          ...options,
          credentials: "include",
        });
      } else {
        const currentUser = store.getState().currentUserSlice.value;
        const userId = currentUser?.id;
        if (userId) {
          sessionStorage.setItem(
            `lastPage:${userId}`,
            window.location.pathname,
          );
        }

        login();
        throw new UserNeedsToLogin("Token refresh failed");
      }
    }

    if (response.status === 410) {
      const refreshResponse = await fetch(`${API_BASE}/auth/refresh-token/`, {
        method: "GET",
        credentials: "include",
      });

      if (refreshResponse.ok) {
        response = await fetch(url, {
          ...options,
          credentials: "include",
        });
      } else {
        return {
          error: true,
          status: 410,
          message: "Token refresh failed. Please log in again.",
        };
      }
    }

    if (response.status === 500) {
      return {
        error: true,
        status: 500,
        message:
          "There appears to be an issue with the LLM you selected, please try using a different language model.",
      };
    }

    if (response.status === 529) {
      return {
        error: true,
        status: 529,
        message:
          "Apologies for the inconvenience. Our AI service is currently experiencing high traffic, causing a temporary overload. Please try using a different language model",
      };
    }

    if (response.status === 429) {
      return {
        error: true,
        status: 429,
        message:
          "Rate limit exceeded. Please try using a different language model.",
      };
    }

    if (!response.ok) {
      let detailMessage;
      try {
        const errorBody = await response.json();
        detailMessage = errorBody?.detail || errorBody?.message;
      } catch {
        detailMessage = undefined;
      }
      return {
        error: true,
        status: response.status,
        message:
          detailMessage ||
          `Request failed with status ${response.status}: ${response.statusText}`,
      };
    }

    notifyIfProjectIqActivity(url, options.method);
    return response;
  } catch (error) {
    return {
      error: true,
      status: 500,
      message: "Network error. Please try again later.",
    };
  }
};

export const login = () => {
  window.location.href = `${API_BASE}/auth/login/`;
};

export const logout = async () => {
  try {
    await requestWithCookieAuth(`${API_BASE}/auth/logout/`, {
      method: "GET",
      credentials: "include",
    });
  } catch (error) {
    console.error("Logout failed:", error);
  }
};

export const checkAuth = async () => {
  try {
    const response = await requestWithCookieAuth(`${API_BASE}/auth/check/`, {
      method: "GET",
      credentials: "include",
    });
    if (response.ok) {
      return true;
    }
  } catch (error) {
    console.error("Authentication check failed:", error);
  }
  return false;
};

export const getConversationsToReference = async ({ workspaceId }) => {
  const url = new URL(`${API_BASE}/conversations/`);
  url.searchParams.append("workspaceId", workspaceId);
  url.searchParams.append("onlyMyConversations", "false");
  url.searchParams.append("offset", 0);
  url.searchParams.append("ascending", false);

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
    redirect: "follow",
    headers: {
      Accept: "application/json",
    },
  });

  if (response?.error) {
    throw new Error(
      response.message || "Failed to fetch referenceable conversations.",
    );
  }

  return await response.json();
};

export const getConversationsWithCount = async ({
  onlyCopiedConversations = false,
  onlyMyConversations = false,
  favorited = false,
  ascending = true,
  searchText,
  workspaceId,
  limit = 10,
  offset = 0,
}) => {
  let url = new URL(`${API_BASE}/conversations/with-count/`);

  url.searchParams.append("limit", limit);
  url.searchParams.append("offset", offset);

  if (onlyCopiedConversations)
    url.searchParams.append("onlyCopiedConversations", onlyCopiedConversations);
  if (onlyMyConversations)
    url.searchParams.append("onlyMyConversations", onlyMyConversations);
  if (favorited) url.searchParams.append("favorited", favorited);
  if (ascending !== undefined) url.searchParams.append("ascending", ascending);
  if (searchText) url.searchParams.append("searchText", searchText);
  if (workspaceId) url.searchParams.append("workspaceId", workspaceId);

  const response = await requestWithCookieAuth(url.toString(), {
    redirect: "follow",
  });

  if (response?.error) {
    throw new Error(response.message || "Failed to fetch conversations");
  }

  const data = await response.json();
  if (!data?.conversations) {
    throw new Error("Invalid response format from conversations API");
  }
  return data;
};

export const getConversations = async ({
  onlyCopiedConversations = false,
  onlyMyConversations = false,
  favorited = false,
  ascending = true,
  searchText,
  workspaceId,
  limit = 10,
  offset = 0,
}) => {
  const result = await getConversationsWithCount({
    onlyCopiedConversations,
    onlyMyConversations,
    favorited,
    ascending,
    searchText,
    workspaceId,
    limit,
    offset,
  });

  return result.conversations || result;
};

export const getWorkspaceConversations = async ({
  workspaceId,
  limit = 15,
  offset = 0,
}) => {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  const response = await requestWithCookieAuth(
    `${API_BASE}/conversations/workspaces/${workspaceId}/?${params}`,
    {
      method: "GET",
      redirect: "follow",
      headers: {
        Accept: "application/json",
      },
    },
  );
  return await response.json();
};

export const getAllConversations = async ({ month } = {}) => {
  const url = new URL(`${API_BASE}/conversations/export`);

  if (month) {
    url.searchParams.append("month", month);
  }

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
    headers: {
      Accept: "text/csv",
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to export conversations: ${errorText}`);
  }

  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = "conversations_export.csv";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(downloadUrl);
};

export const getCategories = async (returnAllTags) => {
  const url = new URL(`${API_BASE}/categories/`);
  url.searchParams.append("return_all_tags", returnAllTags ? "true" : "false");

  const response = await requestWithCookieAuth(url.toString(), {
    redirect: "follow",
  });
  if (!response.ok) {
    throw new Error("Network response was not ok");
  }

  return await response.json();
};

export const getPrompts = async () => {
  const response = await requestWithCookieAuth(`${API_BASE}/prompts/`, {
    redirect: "follow",
  });
  return await response.json();
};

export const createPrompt = async ({ title, description }) => {
  const response = await requestWithCookieAuth(`${API_BASE}/prompts/`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    redirect: "follow",
    body: JSON.stringify({ title, description }),
  });
  return await response.json();
};

export const deletePrompt = async ({ promptId }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/prompts/${promptId}/`,
    {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    },
  );
  return await response.json();
};

export const getMessagesOfConversation = async ({ conversationId }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/conversations/${conversationId}/messages/?asc=true`,
    { redirect: "follow" },
  );
  if (response?.error) {
    throw response;
  }
  return await response.json();
};

export const deleteConversation = async ({ conversationId }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/conversations/${conversationId}/`,
    { method: "DELETE", redirect: "follow" },
  );
  if (response?.error) {
    throw response;
  }
  return await response.json();
};

export const getAIResponse = async ({
  llmConfig,
  messages,
  persistFiles = true,
  abortSignal = null,
  workspaceLibraryFiles = [],
  workspaceId = "",
  deliveryMethodConfigMethod = null,
  inFocusFiles = [],
  conversationId = null,
}) => {
  let response;
  try {
    response = await requestWithCookieAuth(`${API_BASE}/ai/chat/`, {
      headers: {
        Accept: "text/event-stream",
        "Content-Type": "application/json",
      },
      redirect: "follow",
      method: "POST",
      body: JSON.stringify({
        llmConfig,
        messages,
        persistFiles,
        sourceRagCategories:
          llmConfig?.sourceRagCategories && llmConfig.sourceRagCategories.length
            ? llmConfig.sourceRagCategories
            : null,
        workspaceLibraryFiles:
          workspaceLibraryFiles && workspaceLibraryFiles.length
            ? workspaceLibraryFiles
            : null,
        workspaceId: workspaceId || null,
        deliveryMethodConfigMethod: deliveryMethodConfigMethod || null,
        inFocusFiles: inFocusFiles && inFocusFiles.length ? inFocusFiles : null,
        conversationId: conversationId || null,
      }),
      signal: abortSignal,
    });

    if ("error" in response) {
      return response;
    }

    if (!(response instanceof Response)) {
      console.error("Invalid response object:", response);
      return {
        error: true,
        status: 500,
        message: "Unexpected response format from AI service.",
      };
    }

    if (!response.ok) {
      let errorMessage = "Unknown error occurred.";
      try {
        const errorData = await response.json();
        errorMessage =
          errorData.message ||
          `AI Request Failed with status ${response.status}`;
      } catch (jsonError) {
        errorMessage = `AI Request Failed with status ${response.status}, and error message could not be parsed.`;
      }
      return {
        error: true,
        status: response.status,
        message: errorMessage,
      };
    }

    return response;
  } catch (error) {
    console.error("Error in getAIResponse:", error);
    return {
      error: true,
      status: 500,
      message: "Network error. Please try again later.",
    };
  }
};

export const getAIAbstract = async ({ llmConfig, messages }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/ai/abstractgeneration/`,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      redirect: "follow",
      method: "POST",
      body: JSON.stringify({ llmConfig, messages }),
    },
  );
  return await response.json();
};

export const getAITitle = async ({ llmConfig, messages }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/ai/titlegeneration/`,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      redirect: "follow",
      method: "POST",
      body: JSON.stringify({ llmConfig, messages }),
    },
  );
  return await response.json();
};

export const updateConversationTitle = async ({ conversation, title }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/conversations/${conversation?.id}/`,
    {
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      method: "PATCH",
      body: JSON.stringify({ title }),
    },
  );
  if (response?.error) {
    throw response;
  }
  return await response.json();
};

export const getConversation = async ({ conversationId }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/conversations/${conversationId}/`,
    { redirect: "follow" },
  );
  if (response?.error) {
    throw response;
  }
  return await response.json();
};

export const addFavoriteConversation = async ({ conversationId }) => {
  const res = await requestWithCookieAuth(
    `${API_BASE}/conversations/${conversationId}/favorite`,
    {
      method: "PATCH",
      headers: { Accept: "application/json" },
      redirect: "follow",
    },
  );
  if (res?.error) {
    throw new Error(res.message || "Failed to favorite");
  }
  return true;
};

export const removeFavoriteConversation = async ({ conversationId }) => {
  const res = await requestWithCookieAuth(
    `${API_BASE}/conversations/${conversationId}/unfavorite`,
    {
      method: "PATCH",
      headers: { Accept: "application/json" },
      redirect: "follow",
    },
  );
  if (res?.error) {
    throw new Error(res.message || "Failed to unfavorite");
  }
  return true;
};

export const setConversationToSharing = async ({ conversation }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/conversations/${conversation?.id}/`,
    {
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      method: "PATCH",
      body: JSON.stringify({}),
    },
  );
  if (response?.error) {
    throw response;
  }
  return await response.json();
};

export const addCommentToMessage = async ({ messageId, content }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/conversations/messages/${messageId}/comments/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ content }),
    },
  );

  if (response?.error) {
    throw response;
  }

  return await response.json();
};

export const addMessageToConversation = async ({ conversationId, message }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/conversations/${conversationId}/messages/`,
    {
      redirect: "follow",
      headers: {
        Accept: "text/event-stream",
        "Content-Type": "application/json",
      },
      method: "POST",
      body: JSON.stringify({ ...message }),
    },
  );
  if (response?.error) {
    throw response;
  }
  return await response.json();
};

export const createConversation = async ({ conversation }) => {
  const response = await requestWithCookieAuth(`${API_BASE}/conversations/`, {
    redirect: "follow",
    headers: {
      Accept: "text/event-stream",
      "Content-Type": "application/json",
    },
    method: "POST",
    body: JSON.stringify(conversation),
  });
  if (response?.error) {
    throw response;
  }
  return await response.json();
};

export const moveConversation = async ({ conversationId, workspaceId }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/conversations/${conversationId}/move/`,
    {
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      method: "POST",
      body: JSON.stringify({ workspaceId }),
    },
  );

  if (response?.error) {
    throw response;
  }
  return await response.json();
};

export const updateConversation = async ({ conversation }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/conversations/${conversation?.id}/`,
    {
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      method: "PUT",
      body: JSON.stringify({ ...conversation }),
    },
  );

  if (response?.error) {
    throw response;
  }
  return await response.json();
};

export const updateContentOfConversation = async ({
  conversationId,
  messageId,
  content,
}) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/conversations/${conversationId}/messages/${messageId}/`,
    {
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      method: "PATCH",
      body: JSON.stringify({ content }),
    },
  );

  if (response?.error) {
    throw response;
  }
  return await response.json();
};

export const getSignedUrl = async ({
  filename,
  entityType,
  entityId,
  conversationId,
  fileData,
  folderPath,
}) => {
  const body = {
    filename,
    entityType,
    entityId,
    conversationId,
    fileData, // Base64 encoded first 5MB of file for validation
  };
  if (folderPath !== undefined) {
    body.folderPath = folderPath;
  }
  const response = await requestWithCookieAuth(
    `${API_BASE}/files/getSignedUrl/`,
    {
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      method: "POST",
      body: JSON.stringify(body),
    },
  );

  if (response.error) {
    return response;
  }

  return await response.json();
};

export const getDownloadSignedUrl = async ({ filename }) => {
  const response = await requestWithCookieAuth(`${API_BASE}/files/`, {
    redirect: "follow",
    body: JSON.stringify({ filename }),
    headers: {
      Accept: "text/event-stream",
      "Content-Type": "application/json",
    },
    method: "POST",
  });

  return await response.json();
};

export const getCurrentUser = async () => {
  const response = await requestWithCookieAuth(`${API_BASE}/auth/me/`, {
    redirect: "follow",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });

  if (response.error || !response.ok) {
    return null;
  }

  return await response.json();
};

export const getUserIdentity = async ({ role }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/users/identity/?role=${role}`,
    {
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    },
  );

  return await response.json();
};

export const updateCategory = async ({ category }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/categories/${category.id}/`,
    {
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      method: "PUT",
      body: JSON.stringify({ ...category }),
    },
  );

  return await response.json();
};

export const deleteCategory = async ({ categoryId }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/categories/${categoryId}/`,
    {
      redirect: "follow",
      method: "DELETE",
    },
  );

  return await response.json();
};

export const createCategory = async ({ category }) => {
  const url = `${API_BASE}/categories/`;

  const response = await requestWithCookieAuth(url, {
    redirect: "follow",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    method: "POST",
    body: JSON.stringify({ ...category }),
  });

  return await response.json();
};

export const getUsers = async ({
  text = "",
  hasOnlyOurUsers = false,
  requiredRoles = [],
} = {}) => {
  let url = `${API_BASE}/users/`;

  const queryParams = [];
  if (text !== undefined) queryParams.push(`text=${encodeURIComponent(text)}`);

  if (hasOnlyOurUsers) queryParams.push(`onlyOurUsers=${hasOnlyOurUsers}`);

  if (requiredRoles.length > 0) {
    requiredRoles.forEach((role) => {
      queryParams.push(`requiredRoles=${encodeURIComponent(role)}`);
    });
  }

  if (queryParams.length > 0) {
    url += `?${queryParams.join("&")}`;
  }

  const response = await requestWithCookieAuth(url, {
    method: "GET",
  });

  const data = await response.json();

  return data;
};

export const addUserToGroup = async (groupName, userId) => {
  try {
    const response = await requestWithCookieAuth(
      `${API_BASE}/users/addUserToGroup/`,
      {
        redirect: "follow",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify({
          userId: userId,
          groupName: groupName,
        }),
      },
    );

    if (!response.ok) {
      throw new Error("Failed to add user to group");
    }

    return await response.json();
  } catch (error) {
    console.error("Failed to add user to group", error);
    return;
  }
};

export const removeUserFromGroup = async (groupName, userId) => {
  try {
    const response = await requestWithCookieAuth(
      `${API_BASE}/users/removeUserFromGroup/`,
      {
        redirect: "follow",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        method: "DELETE",
        body: JSON.stringify({
          userId: userId,
          groupName: groupName,
        }),
      },
    );

    if (!response.ok) {
      throw new Error("Failed to add user to group");
    }

    return await response.json();
  } catch (error) {
    console.error("Failed to add user to group", error);
    return;
  }
};

export const removeUserFromGroups = async (userId) => {
  try {
    const response = await requestWithCookieAuth(
      `${API_BASE}/users/removeUserFromGroups/${userId}/`,
      {
        redirect: "follow",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        method: "DELETE",
      },
    );

    if (!response.ok) {
      throw new Error("Failed to remove user from groups");
    }

    return await response.json();
  } catch (error) {
    console.error("Failed to remove user from groups", error);
    return;
  }
};

export const getUserActivityWithinTimeframe = async ({
  startDate,
  endDate,
}) => {
  let url = new URL(`${API_BASE}/statistics/user-activity/?aslist=0`);
  if (startDate) url.searchParams.append("start_date", startDate.toISOString());
  if (endDate) url.searchParams.append("end_date", endDate.toISOString());

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
  });
  return await response.json();
};

export const getCopiedConversationCount = async ({ startDate, endDate }) => {
  let url = new URL(`${API_BASE}/statistics/copy-count/?aslist=0`);
  if (startDate) url.searchParams.append("start_date", startDate.toISOString());
  if (endDate) url.searchParams.append("end_date", endDate.toISOString());

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
  });
  return await response.json();
};

export const getCopiedConversationCountAsBins = async ({
  startDate,
  endDate,
}) => {
  let url = new URL(`${API_BASE}/statistics/copy-count/?aslist=1`);
  if (startDate) url.searchParams.append("start_date", startDate.toISOString());
  if (endDate) url.searchParams.append("end_date", endDate.toISOString());

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
  });
  return await response.json();
};

export const getUploadedFilesCount = async ({ startDate, endDate }) => {
  let url = new URL(`${API_BASE}/statistics/file-uploads/?aslist=0`);
  if (startDate) url.searchParams.append("start_date", startDate.toISOString());
  if (endDate) url.searchParams.append("end_date", endDate.toISOString());

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
  });
  return await response.json();
};

export const getUploadedFilesCountAsBins = async ({ startDate, endDate }) => {
  let url = new URL(`${API_BASE}/statistics/file-uploads/?aslist=1`);
  if (startDate) url.searchParams.append("start_date", startDate.toISOString());
  if (endDate) url.searchParams.append("end_date", endDate.toISOString());

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
  });
  return await response.json();
};

export const getTagsUsageCount = async ({ startDate, endDate }) => {
  let url = new URL(`${API_BASE}/statistics/tags-usage/?aslist=0`);
  if (startDate) url.searchParams.append("start_date", startDate.toISOString());
  if (endDate) url.searchParams.append("end_date", endDate.toISOString());

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
  });
  return await response.json();
};

export const getPromptUsage = async ({ startDate, endDate }) => {
  let url = new URL(`${API_BASE}/statistics/prompt-usage/?aslist=0`);
  if (startDate) url.searchParams.append("start_date", startDate.toISOString());
  if (endDate) url.searchParams.append("end_date", endDate.toISOString());

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
  });
  return await response.json();
};

export const getPromptUsagePerDay = async ({ startDate, endDate }) => {
  let url = new URL(`${API_BASE}/statistics/prompt-usage/?aslist=1`);
  if (startDate) url.searchParams.append("start_date", startDate.toISOString());
  if (endDate) url.searchParams.append("end_date", endDate.toISOString());

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
  });
  return await response.json();
};

export const getPromptUsageEvents = async ({ startDate, endDate }) => {
  let url = new URL(`${API_BASE}/statistics/prompt-usage-events/`);
  if (startDate) url.searchParams.append("start_date", startDate.toISOString());
  if (endDate) url.searchParams.append("end_date", endDate.toISOString());

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
  });
  return await response.json();
};

export const getTotalDiagnosticsCreated = async ({ startDate, endDate }) => {
  let url = new URL(`${API_BASE}/statistics/diagnostics-created/?aslist=0`);
  if (startDate) url.searchParams.append("start_date", startDate.toISOString());
  if (endDate) url.searchParams.append("end_date", endDate.toISOString());

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
  });
  return await response.json();
};

export const getTotalObjectivesCreated = async ({ startDate, endDate }) => {
  let url = new URL(`${API_BASE}/statistics/objectives-created/?aslist=0`);
  if (startDate) url.searchParams.append("start_date", startDate.toISOString());
  if (endDate) url.searchParams.append("end_date", endDate.toISOString());

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
  });
  return await response.json();
};

export const getTotalDistributionsCreated = async ({ startDate, endDate }) => {
  let url = new URL(`${API_BASE}/statistics/distributions-created/?aslist=0`);
  if (startDate) url.searchParams.append("start_date", startDate.toISOString());
  if (endDate) url.searchParams.append("end_date", endDate.toISOString());

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
  });
  return await response.json();
};

export const getCompletedInterviews = async ({ startDate, endDate }) => {
  let url = new URL(`${API_BASE}/statistics/completed-interviews/?aslist=0`);
  if (startDate) url.searchParams.append("start_date", startDate.toISOString());
  if (endDate) url.searchParams.append("end_date", endDate.toISOString());

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
  });
  return await response.json();
};

export const getStartedInterviews = async ({ startDate, endDate }) => {
  let url = new URL(`${API_BASE}/statistics/started-interviews/?aslist=0`);
  if (startDate) url.searchParams.append("start_date", startDate.toISOString());
  if (endDate) url.searchParams.append("end_date", endDate.toISOString());

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
  });
  return await response.json();
};

export const addDiagnosticCreated = async ({ diagnosticId }) => {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/add-diagnostic-created/`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ diagnosticId }),
    },
  );
  return await response.json();
};

export const addObjectiveCreated = async ({ objectiveId }) => {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/add-objective-created/`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ objectiveId }),
    },
  );
  return await response.json();
};

export const addCompletedInterview = async () => {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/external-diagnostics/add-completed-interview/`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    },
  );
  return await response.json();
};

export const addStartedInterview = async () => {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/external-diagnostics/add-started-interview/`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    },
  );
  return await response.json();
};

export const addUseCasePromptUsage = async ({ promptId, title, useCaseId }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/usecases/add-prompt-usage/`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ promptId, title, useCaseId }),
      redirect: "follow",
    },
  );
  return await response.json();
};

export const trackConversationClick = async ({ conversationId }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/conversations/conversation-click/`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ conversation_id: conversationId }),
      redirect: "follow",
    },
  );
  return await response.json();
};

export const trackConversationCopy = async ({ conversationId }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/conversations/conversation-copy/`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ conversation_id: conversationId }),
      redirect: "follow",
    },
  );
  return await response.json();
};

export const getTagsUsageCountAsBins = async ({ startDate, endDate }) => {
  let url = new URL(`${API_BASE}/statistics/tags-usage/?aslist=1`);
  if (startDate) url.searchParams.append("start_date", startDate.toISOString());
  if (endDate) url.searchParams.append("end_date", endDate.toISOString());

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
  });
  return await response.json();
};

export const getUserActivityWithinTimeframeAsBins = async ({
  startDate,
  endDate,
}) => {
  let url = new URL(`${API_BASE}/statistics/user-activity/?aslist=1`);
  if (startDate) url.searchParams.append("start_date", startDate.toISOString());
  if (endDate) url.searchParams.append("end_date", endDate.toISOString());

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
  });
  return await response.json();
};

export const getNHQAUsage = async ({ startDate, endDate }) => {
  let url = new URL(`${API_BASE}/statistics/nhqa-usage/?aslist=0`);
  if (startDate) url.searchParams.append("start_date", startDate.toISOString());
  if (endDate) url.searchParams.append("end_date", endDate.toISOString());

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
  });
  return await response.json();
};

export const getNHQAUsagePerDay = async ({ startDate, endDate }) => {
  let url = new URL(`${API_BASE}/statistics/nhqa-usage/?aslist=1`);
  if (startDate) url.searchParams.append("start_date", startDate.toISOString());
  if (endDate) url.searchParams.append("end_date", endDate.toISOString());

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
  });
  return await response.json();
};

export const getTemporaryChatUserUsage = async ({ startDate, endDate }) => {
  let url = new URL(`${API_BASE}/statistics/temporary-chat-user-usage/`);
  if (startDate) url.searchParams.append("start_date", startDate.toISOString());
  if (endDate) url.searchParams.append("end_date", endDate.toISOString());

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
  });
  return await response.json();
};

export const getConverationsClickedUsage = async ({ startDate, endDate }) => {
  let url = new URL(`${API_BASE}/statistics/conversation-click-count/`);
  if (startDate) url.searchParams.append("start_date", startDate.toISOString());
  if (endDate) url.searchParams.append("end_date", endDate.toISOString());

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
  });
  return await response.json();
};

export const addTemporaryChatUserUsage = async () => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/ai/add-temporary-chat-user-usage/`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      redirect: "follow",
    },
  );
  return await response.json();
};

export const downloadListOfUsersThatInteractedWithOurApplication = async ({
  startDate,
  endDate,
}) => {
  let url = new URL(`${API_BASE}/statistics/users-that-interacted/`);
  if (startDate) url.searchParams.append("start_date", startDate.toISOString());
  if (endDate) url.searchParams.append("end_date", endDate.toISOString());

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
  });

  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = downloadUrl;
  a.download = `users_${startDate?.toISOString()?.substring(0, 10)}_${endDate?.toISOString()?.substring(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(downloadUrl);
};

export const getModelUsage = async ({ startDate, endDate }) => {
  let url = new URL(`${API_BASE}/statistics/model-usage/?aslist=0`);
  if (startDate) url.searchParams.append("start_date", startDate.toISOString());
  if (endDate) url.searchParams.append("end_date", endDate.toISOString());

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
  });
  return await response.json();
};

export const getModelUsagePerDays = async ({ startDate, endDate }) => {
  let url = new URL(`${API_BASE}/statistics/model-usage/?aslist=1`);
  if (startDate) url.searchParams.append("start_date", startDate.toISOString());
  if (endDate) url.searchParams.append("end_date", endDate.toISOString());

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
  });
  return await response.json();
};

export const getWorkspacesUsageAsBins = async ({ startDate, endDate }) => {
  let url = new URL(`${API_BASE}/statistics/workspaces-usage/?aslist=1`);
  if (startDate) url.searchParams.append("start_date", startDate.toISOString());
  if (endDate) url.searchParams.append("end_date", endDate.toISOString());

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
  });
  return await response.json();
};

export const getWorkspacesUsageCount = async ({ startDate, endDate }) => {
  let url = new URL(`${API_BASE}/statistics/workspaces-usage/?aslist=0`);
  if (startDate) url.searchParams.append("start_date", startDate.toISOString());
  if (endDate) url.searchParams.append("end_date", endDate.toISOString());

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
  });
  return await response.json();
};

export const getWorkspaceLibraryDocumentsAsBins = async ({
  startDate,
  endDate,
}) => {
  let url = new URL(
    `${API_BASE}/statistics/workspace-library-documents/?aslist=1`,
  );
  if (startDate) url.searchParams.append("start_date", startDate.toISOString());
  if (endDate) url.searchParams.append("end_date", endDate.toISOString());

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
  });

  return await response.json();
};

export const getWorkspaceLibraryDocumentsCount = async ({
  startDate,
  endDate,
}) => {
  let url = new URL(
    `${API_BASE}/statistics/workspace-library-documents/?aslist=0`,
  );
  if (startDate) url.searchParams.append("start_date", startDate.toISOString());
  if (endDate) url.searchParams.append("end_date", endDate.toISOString());

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
  });

  return await response.json();
};

export const getMcpTotalUsage = async ({ startDate, endDate }) => {
  let url = new URL(`${API_BASE}/statistics/mcp-total-usage/?aslist=0`);
  if (startDate) url.searchParams.append("start_date", startDate.toISOString());
  if (endDate) url.searchParams.append("end_date", endDate.toISOString());

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
  });
  return await response.json();
};

export const getMcpTotalUsageAsBins = async ({ startDate, endDate }) => {
  let url = new URL(`${API_BASE}/statistics/mcp-total-usage/?aslist=1`);
  if (startDate) url.searchParams.append("start_date", startDate.toISOString());
  if (endDate) url.searchParams.append("end_date", endDate.toISOString());

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
  });
  return await response.json();
};

export const getMcpUsageSummary = async ({ startDate, endDate }) => {
  let url = new URL(`${API_BASE}/statistics/mcp-usage-summary/`);
  if (startDate) url.searchParams.append("start_date", startDate.toISOString());
  if (endDate) url.searchParams.append("end_date", endDate.toISOString());

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
  });
  return await response.json();
};

export const getMcpUniqueUsers = async ({ startDate, endDate }) => {
  let url = new URL(`${API_BASE}/statistics/mcp-unique-users/`);
  if (startDate) url.searchParams.append("start_date", startDate.toISOString());
  if (endDate) url.searchParams.append("end_date", endDate.toISOString());

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
  });
  return await response.json();
};

export const exportToExcel = async () => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/files/exportToExcel/`,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      redirect: "follow",
    },
  );
  const blob = await response.blob();
  saveAs(blob, "Users.xlsx");
};

export const createAnnouncement = async ({ title, content, type, enabled }) => {
  const response = await requestWithCookieAuth(`${API_BASE}/announcements/`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    redirect: "follow",
    body: JSON.stringify({ title, content, type, enabled }),
  });
  return await response.json();
};

export const getAnnouncements = async ({ onlyEnabled, onlyNotSeen } = {}) => {
  const queryParams = [];
  if (onlyEnabled !== undefined)
    queryParams.push(`only_enabled=${onlyEnabled}`);
  if (onlyNotSeen !== undefined)
    queryParams.push(`only_not_seen=${onlyNotSeen}`);

  const url = `${API_BASE}/announcements/${queryParams.length ? `?${queryParams.join("&")}` : ConstantValues.EMPTY_STRING}`;
  const response = await requestWithCookieAuth(url, { method: "GET" });
  return await response.json();
};

export const updateAnnouncement = async ({
  id,
  title,
  content,
  enabled,
  type,
}) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/announcements/${id}/`,
    {
      method: "PUT",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      redirect: "follow",
      body: JSON.stringify({ title, content, enabled, type }),
    },
  );
  return await response.json();
};

export const deleteAnnouncement = async ({ id }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/announcements/${id}/`,
    { method: "DELETE" },
  );
  return await response.json();
};

export const markAnnouncementAsSeen = async ({ id }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/announcements/${id}/mark-seen/`,
    { method: "POST" },
  );
  return await response.json();
};

export const markAllUsersAnnouncementsAsSeen = async () => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/announcements/mark-all-seen/`,
    { method: "POST" },
  );
  return await response.json();
};

export const createFeedback = async ({ feedback }) => {
  const response = await requestWithCookieAuth(`${API_BASE}/feedback/`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    redirect: "follow",
    body: JSON.stringify(feedback),
  });
  return await response.json();
};

export const getFeedback = async () => {
  const response = await requestWithCookieAuth(`${API_BASE}/feedback/`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    redirect: "follow",
  });

  return await response.json();
};

export const geFeedbackById = async ({ feedbackId }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/feedback/${feedbackId}/`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      redirect: "follow",
    },
  );

  return await response.json();
};

export const getAccessLLMModels = async () => {
  const response = await requestWithCookieAuth(`${API_BASE}/accessLLMModels/`, {
    method: "GET",
  });
  return await response.json();
};

export const getAllAccessLLMModels = async () => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/accessLLMModels/admin/`,
    { method: "GET" },
  );
  return await response.json();
};

export const createAccessLLMModel = async ({
  name,
  accessType,
  allowedUsers,
}) => {
  const response = await requestWithCookieAuth(`${API_BASE}/accessLLMModels/`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    redirect: "follow",
    body: JSON.stringify({ name, accessType, allowedUsers }),
  });
  return await response.json();
};

export const updateAccessLLMModel = async ({
  modelId,
  accessType,
  displayName,
  allowedUsers,
  order,
}) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/accessLLMModels/${modelId}/`,
    {
      method: "PUT",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      redirect: "follow",
      body: JSON.stringify({ accessType, allowedUsers, displayName, order }),
    },
  );
  return await response.json();
};

export const deleteAccessLLMModel = async ({ modelId }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/accessLLMModels/${modelId}/`,
    {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      redirect: "follow",
    },
  );
  return await response.json();
};

export const getUseCases = async () => {
  const response = await requestWithCookieAuth(`${API_BASE}/usecases/`, {
    method: "GET",
    redirect: "follow",
    headers: {
      Accept: "application/json",
    },
  });
  return await response.json();
};

export const createUseCase = async ({ title }) => {
  const response = await requestWithCookieAuth(`${API_BASE}/usecases/`, {
    method: "POST",
    redirect: "follow",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ title }),
  });
  return await response.json();
};

export const updateUseCase = async ({ id, usecase }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/usecases/?id=${id}`,
    {
      method: "PUT",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(usecase),
      redirect: "follow",
    },
  );
  return await response.json();
};

export const movePrompt = async ({
  prompt_id,
  from_use_case_id,
  to_use_case_id,
  prompt,
}) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/usecases/move-prompt`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        prompt_id,
        from_use_case_id,
        to_use_case_id,
        prompt,
      }),
      redirect: "follow",
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to move prompt: ${errorText}`);
  }

  return await response.json();
};

export const rateUseCasePrompt = async ({ promptId, value }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/usecases/prompts/${promptId}/rating`,
    {
      method: "PATCH",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      redirect: "follow",
      body: JSON.stringify({ value }),
    },
  );

  if (response?.error) {
    throw new Error(response.message || "Failed to rate prompt");
  }

  return true;
};

export const deleteUseCase = async ({ useCaseId }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/usecases/${useCaseId}/`,
    {
      method: "DELETE",
      redirect: "follow",
    },
  );

  return await response.json();
};

export const addFavoriteUseCasePrompt = async ({ promptId }) => {
  const res = await requestWithCookieAuth(
    `${API_BASE}/usecases/prompts/${promptId}/favorite`,
    {
      method: "PATCH",
      headers: { Accept: "application/json" },
      redirect: "follow",
    },
  );

  if (res?.error) {
    throw new Error(res.message || "Failed to favorite");
  }

  return true;
};

export const removeFavoriteUseCasePrompt = async ({ promptId }) => {
  const res = await requestWithCookieAuth(
    `${API_BASE}/usecases/prompts/${promptId}/unfavorite`,
    {
      method: "PATCH",
      headers: { Accept: "application/json" },
      redirect: "follow",
    },
  );

  if (res?.error) {
    throw new Error(res.message || "Failed to unfavorite");
  }

  return true;
};

export const getUseCasePrompts = async (useCaseId = null) => {
  const url = new URL(`${API_BASE}/usecases/prompts/`);
  if (useCaseId) {
    url.searchParams.append("use_case_id", useCaseId);
  }

  const response = await requestWithCookieAuth(url.toString(), {
    method: "GET",
    redirect: "follow",
    headers: {
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.detail || "Failed to fetch use case prompts");
  }

  return await response.json();
};

export const getUseCasePrompt = async ({ promptId }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/usecases/prompts/${promptId}/`,
    {
      method: "GET",
      redirect: "follow",
      headers: {
        Accept: "application/json",
      },
    },
  );

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.detail || "Failed to fetch use case prompt");
  }

  return await response.json();
};

export const createUseCasePrompt = async ({ prompt }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/usecases/prompts/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(prompt),
    },
  );

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.detail || "Failed to create use case prompt");
  }

  return await response.json();
};

export const updateUseCasePrompt = async ({ promptId, prompt }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/usecases/prompts/${promptId}/`,
    {
      method: "PUT",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(prompt),
    },
  );

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.detail || "Failed to update use case prompt");
  }

  return await response.json();
};

export const deleteUseCasePrompt = async ({ promptId }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/usecases/prompts/${promptId}/`,
    {
      method: "DELETE",
      redirect: "follow",
      headers: {
        Accept: "application/json",
      },
    },
  );

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.detail || "Failed to delete use case prompt");
  }

  // DELETE returns 204 No Content - no body to parse
  return true;
};

export const getAllWorkspaces = async () => {
  const response = await requestWithCookieAuth(`${API_BASE}/workspaces/`, {
    method: "GET",
    redirect: "follow",
    headers: {
      Accept: "application/json",
    },
  });
  return await response.json();
};

export const getMyWorkspaces = async () => {
  const response = await requestWithCookieAuth(`${API_BASE}/workspaces/mine/`, {
    method: "GET",
    redirect: "follow",
    headers: {
      Accept: "application/json",
    },
  });
  return await response.json();
};

export const createWorkspace = async ({ workspace }) => {
  const response = await requestWithCookieAuth(`${API_BASE}/workspaces/`, {
    method: "POST",
    redirect: "follow",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(workspace),
  });
  return await response.json();
};

export const updateWorkspace = async ({ id, workspace }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/workspaces/?id=${id}`,
    {
      method: "PUT",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(workspace),
    },
  );
  return await response.json();
};

export const archiveWorkspace = async ({ id }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/workspaces/${id}/archive`,
    {
      method: "PATCH",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    },
  );

  return await response.json();
};

export const unArchiveWorkspace = async ({ id }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/workspaces/${id}/unarchive`,
    {
      method: "PATCH",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    },
  );

  return await response.json();
};

export const deleteWorkspace = async ({ workspaceId }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/workspaces/${workspaceId}/`,
    {
      method: "DELETE",
      redirect: "follow",
    },
  );
  return await response.json();
};

// Workspace Library API Methods
export const getWorkspaceLibraryDocuments = async ({ workspaceId }) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/workspaces/${workspaceId}/library/`,
    {
      method: "GET",
      redirect: "follow",
      headers: {
        Accept: "application/json",
      },
    },
  );
  return await response.json();
};

export const addWorkspaceLibraryDocument = async ({
  workspaceId,
  filename,
  originalFilename,
}) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/workspaces/${workspaceId}/library/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        workspaceId,
        filename,
        originalFilename,
      }),
    },
  );
  return await response.json();
};

export const deleteWorkspaceLibraryDocument = async ({
  workspaceId,
  filename,
}) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/workspaces/${workspaceId}/library/`,
    {
      method: "DELETE",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        workspaceId,
        filename,
      }),
    },
  );
  return await response.json();
};

export const getWorkspaceLibraryDocumentDownloadUrl = async ({
  workspaceId,
  filename,
}) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/workspaces/${workspaceId}/library/download-url/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ filename }),
    },
  );
  if (response?.error) {
    throw new Error(
      response.message ||
        "Failed to get workspace library document download URL",
    );
  }
  return await response.json();
};

export const sendCopyNotificationEmail = async ({
  recipients,
  subject,
  body,
}) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/conversations/send-copy-notification/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ recipients, subject, body }),
    },
  );

  if (response.error) {
    throw new Error(
      `Failed to send email: ${response.message ?? "Unknown error"}`,
    );
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to send email: ${errorText}`);
  }

  return await response.json();
};

export const getTemplates = async () => {
  const response = await requestWithCookieAuth(`${API_BASE}/templates/`, {
    redirect: "follow",
  });
  return await response.json();
};

export const getTemplatesAdmin = async () => {
  const response = await requestWithCookieAuth(`${API_BASE}/templates/admin`, {
    redirect: "follow",
  });
  return await response.json();
};

export const updateTemplate = async (templateId, updateData) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/templates/${templateId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updateData),
      redirect: "follow",
    },
  );
  return await response.json();
};

export const createTemplate = async (templateData) => {
  const response = await requestWithCookieAuth(`${API_BASE}/templates/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(templateData),
  });
  const data = await response.json();
  return data;
};

export const deleteTemplate = async (templateId) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/templates/${templateId}`,
    {
      method: "DELETE",
    },
  );
  const data = await response.json();
  return data;
};

export const getHelpPageDocuments = async () => {
  const response = await requestWithCookieAuth(`${API_BASE}/helpknowledge/`, {
    redirect: "follow",
  });
  if (!response.ok) {
    throw new Error("Failed to fetch help page documents");
  }
  return await response.json();
};

export const deleteHelpPageDocument = async (documentId) => {
  const response = await requestWithCookieAuth(
    `${API_BASE}/helpknowledge/${documentId}`,
    {
      method: "DELETE",
    },
  );
  if (!response.ok) {
    throw new Error("Failed to delete document");
  }
  return await response.json();
};

export const createHelpPageDocument = async (documentData) => {
  const response = await requestWithCookieAuth(`${API_BASE}/helpknowledge/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(documentData),
  });
  if (!response.ok) {
    throw new Error("Failed to create document");
  }
  return await response.json();
};

export const addDiagnosticResultFile = async ({ diagnosticId, file }) => {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${diagnosticId}/result-files/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(file),
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to add result file");
  }
  return await response.json();
};

export const deleteDiagnosticResultFile = async ({ diagnosticId, url }) => {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${diagnosticId}/result-files/`,
    {
      method: "DELETE",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ url }),
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to delete result file");
  }
  if (response.status === 204) {
    return null;
  }
  return await response.json();
};

export const replaceDiagnosticResultFile = async ({
  diagnosticId,
  oldUrl,
  file,
}) => {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${diagnosticId}/result-files/`,
    {
      method: "PUT",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ oldUrl, ...file }),
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to replace result file");
  }
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body?.detail || "Failed to replace result file");
  }
  return await response.json();
};

export const saveDiagnosticSummary = async ({ diagnosticId, summary }) => {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${diagnosticId}/summary/`,
    {
      method: "PATCH",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ summary }),
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to save summary");
  }
  return await response.json();
};

export const exportDiagnosticDocx = async ({
  diagnosticId,
  conversationId,
  messageId,
}) => {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${diagnosticId}/export-docx/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept:
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ conversationId, messageId }),
    },
  );
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to export document: ${errorText}`);
  }
  return await response.blob();
};

export const setDiagnosticStatus = async ({ diagnosticId, status }) => {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${diagnosticId}/status/`,
    {
      method: "PATCH",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ status }),
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to set diagnostic status");
  }
  return await response.json();
};
